// SmoothSpeedometer.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactSpeedometer from "react-d3-speedometer";

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
const easeInOutCubic = (t) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

function SmoothSpeedometer({
  value,
  min = 0,
  max = 100,
  duration = 1200,
  width = 260,
  height = 140,
  ringWidth = 20,
}) {
  // Start from min so first render pe full left→right sweep mile
  const [animated, setAnimated] = useState(min);
  const prev = useRef(min);
  const raf = useRef(0);

  useEffect(() => {
    const from = prev.current;
    const to = clamp(value, min, max);
    cancelAnimationFrame(raf.current);
    const t0 = performance.now();

    const step = (now) => {
      const p = Math.min(1, (now - t0) / duration);
      setAnimated(from + (to - from) * easeInOutCubic(p));
      if (p < 1) raf.current = requestAnimationFrame(step);
      else prev.current = to;
    };

    raf.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf.current);
  }, [value, min, max, duration]);

  // Stable references to avoid transition resets
  const stops = useMemo(() => [0, 20, 40, 60, 80, 100], []);
  const colors = useMemo(
    () => ["#dc3545", "#fd7e14", "#ffc107", "#20c997", "#28a745"],
    []
  );

  return (
    <ReactSpeedometer
      minValue={min}
      maxValue={max}
      value={animated}
      width={width}
      height={height}
      ringWidth={ringWidth}
      needleHeightRatio={0.8}
      needleColor="#2c3e50"
      customSegmentStops={stops}
      segmentColors={colors}
      textColor="transparent"
      // Animate externally for jank‑free motion
      needleTransition="linear"
      needleTransitionDuration={0}
      forceRender={false}
    />
  );
}

export default React.memo(SmoothSpeedometer);
